import { Link } from "react-router-dom";
import pilotMovies from "../data/pilot_support20_onboarding.json";
import pilotManifest from "../data/pilot_support20_onboarding.manifest.json";
import servingDeployment from "../data/serving_deployment.json";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  resolveMovieLensRecommendation,
  filterRecommendationRecords,
} from "../utils/tmdbMetadata.mjs";
import {
  canonicalMovieLensId,
  deduplicateSelectableCatalog,
  appendCatalogPage,
  selectableCatalogMovieIds,
} from "../utils/selectableCatalog.mjs";
import { publicAsset } from "../utils/publicAsset.mjs";
import { apiErrorMessage } from "../utils/apiError.mjs";
import { PILOT_API_URL } from "../utils/apiConfig";
import { useStudySession } from "../study/useStudySession";
import { useRecommendationSession, useSessionValue } from "../study/useRecommendationSession";
import { moviePosterMetadata } from "../utils/posterMetadata.mjs";
import MoviePoster from "./MoviePoster";
import MoviePosterPreview from "./MoviePosterPreview";
import RecommendationGrid from "./RecommendationGrid";
import SelectedMovies from "./SelectedMovies";
import FiveStarRating from "./FiveStarRating";
import { useMovieRatings } from "../study/useMovieRatings";
import { usePageRefresh } from "../study/pageRefresh";
import {
  recommendationInputSnapshot,
  responseMatchesCurrent,
  summaryInputSnapshot,
} from "../study/studyProtocol.mjs";
const TEARS_ALPHA = 0.5;
const MIN_RECOMMENDATION_YEAR = servingDeployment.candidate_filter.value;
const SELECTABLE_MOVIES = deduplicateSelectableCatalog(pilotMovies);
const CATALOG_IDS = selectableCatalogMovieIds(SELECTABLE_MOVIES);



/* ---------------------------------------------------------
    TMDB SEARCH + POSTER
----------------------------------------------------------*/
async function fetchPoster(movieId, title, tmdbId = null, imdbId = null) {
  return moviePosterMetadata(movieId, title, tmdbId, imdbId);
}
/* ---------------------------------------------------------
    MAIN TEARS COMPONENT
----------------------------------------------------------*/
export default function TearsApp({ goBack }) {
  const study = useStudySession("TEARS");
  const session = useRecommendationSession("TEARS", study, CATALOG_IDS);
  const { recommendations, setRecommendations, recordCatalog } = session;
  const recommendationsRef = useRef(recommendations);
  recommendationsRef.current = recommendations;
  const [movies, setMovies] = useState([]);
  const [loadingMovies, setLoadingMovies] = useState(true);

  const [selected, setSelected] = useSessionValue(`${session.prefix}:selected`, []);
  const selectedMovieIds = new Set(selectableCatalogMovieIds(selected));
  const catalogMovies = movies.filter((movie) => !selectedMovieIds.has(Number(movie.movieId)));
  const [movieRatings, setMovieRatings] = useMovieRatings("TEARS", study);
  const [summary, setSummary] = useSessionValue(`${session.prefix}:summary`, "");
  const [summarySourceRequestId, setSummarySourceRequestId] = useSessionValue(`${session.prefix}:summarySourceRequestId`, null);
  const [summaryPending, setSummaryPending] = useSessionValue(`${session.prefix}:summaryPending`, false);
  const [summaryError, setSummaryError] = useState("");
  const [recommendationError, setRecommendationError] = useState("");
  const context = "";
  const dislikedGenres = "";
  const [topK, setTopK] = useSessionValue(`${session.prefix}:topK`, 12);

  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const summaryRequestId = useRef(0);
  const recommendationRequestId = useRef(0);
  const representationRevisionRef = useRef(0);
  const activeSummaryRequest = useRef(null);
  const activeRecommendationRequest = useRef(null);
  const currentRecommendationSignature = useRef("dirty-initial");

  const bumpRepresentationRevision = () => {
    representationRevisionRef.current += 1;
    return representationRevisionRef.current;
  };

  const invalidateRecommendations = (clearVisible = true) => {
    session.invalidate();
    recommendationRequestId.current += 1;
    activeRecommendationRequest.current = null;
    currentRecommendationSignature.current = `dirty-${recommendationRequestId.current}`;
    session.setNotice("");
    if (clearVisible) {
      setRecommendations([]);
    }
    setLoading(false);
  };

  /* ---------------------------------------------------------
      LOAD MOVIES
  ----------------------------------------------------------*/
  useEffect(() => {
  let cancelled = false;
  async function loadMoviesWithPosters() {
    const enriched = await Promise.all(
      SELECTABLE_MOVIES.map(async (m) => {
        const poster = await fetchPoster(m.movieId, m.title, m.tmdbId, m.imdbId);

        return {
          ...m,
          poster: poster?.poster_url || null,
          poster_urls: poster?.poster_urls || [],
          overview: poster?.overview || "",
          rating: poster?.rating ?? null,
          year: poster?.release_year || m.title.match(/\((\d{4})\)\s*$/)?.[1] || "",
        };
      })
    );

    if (cancelled) return;
    const visible = appendCatalogPage([], enriched, recommendationsRef.current);
    recordCatalog(visible);
    setMovies(visible);
    setLoadingMovies(false);
  }

  loadMoviesWithPosters();
  return () => { cancelled = true; };
}, [recordCatalog]);

  /* ---------------------------------------------------------
      SELECT MOVIES → GPT SUMMARIZER (PATCHED)
  ----------------------------------------------------------*/
const requestSummary = async (
  likedMovies,
  nextRatings = movieRatings,
  revision = representationRevisionRef.current
) => {
  const requestId = ++summaryRequestId.current;
  setSummarySourceRequestId(null);
  if (likedMovies.length === 0) {
    setSummaryPending(false);
    setSummary("");
    setSummaryError("");
    setSummaryLoading(false);
    return;
  }

  const unratedMovies = likedMovies.filter(
    (movie) => !Number.isFinite(nextRatings[movie.movieId])
  );
  if (unratedMovies.length > 0) {
    setSummaryPending(false);
    setSummary("");
    setSummaryError(
      `All selected movies must be rated. Unrated: ${unratedMovies
        .map((movie) => movie.title)
        .join(", ")}`
    );
    setSummaryLoading(false);
    return;
  }

  setSummary("");
  setSummaryError("");
  setSummaryLoading(true);
  setSummaryPending(true);
  try {
    const payload = {
      movies: likedMovies.map((movie) => ({
        title: movie.title,
        rating: nextRatings[movie.movieId],
        genres: movie.genres || [],
      })),
      disliked: dislikedGenres.split(",").map((g) => g.trim()).filter(Boolean),
      context,
    };
    const input = summaryInputSnapshot(payload);
    const meta = await study.metadata({
      taskId: "1a",
      input,
      representationRevision: revision,
    });
    payload.study = meta;
    activeSummaryRequest.current = meta;
    const res = await axios.post(`${PILOT_API_URL}/summarize`, payload);
    if (
      requestId === summaryRequestId.current &&
      responseMatchesCurrent(meta, res.data.study, meta.input_signature)
    ) {
      setSummary(res.data.summary || "");
      setSummarySourceRequestId(res.data.summary_source_request_id || null);
      invalidateRecommendations();
    }
  } catch (err) {
    console.error("TEARS summarizer error", err);
    if (requestId === summaryRequestId.current) {
      setSummaryError(
        err.response?.data?.detail || "Summary generation failed. Please try again."
      );
    }
  } finally {
    if (requestId === summaryRequestId.current) {
      setSummaryLoading(false);
      setSummaryPending(false);
    }
  }
};

const toggleSelect = async (movie) => {
  let updated;
  const selectedMovieId = canonicalMovieLensId(movie.movieId);

  if (
    selected.find(
      (m) => canonicalMovieLensId(m.movieId) === selectedMovieId
    )
  ) {
    updated = selected.filter(
      (m) => canonicalMovieLensId(m.movieId) !== selectedMovieId
    );
    // Keep the explicit rating so revisiting this movie restores it.
  } else {
    updated = [...selected, movie];
  }

  setSelected(updated);
  // A selection change makes both visible results and in-flight responses stale.
  invalidateRecommendations();
  const revision = bumpRepresentationRevision();
  await requestSummary(updated, movieRatings, revision);
};

const handleRatingChange = async (movieId, value) => {
  const nextRatings = {
    ...movieRatings,
    [movieId]: Number(value),
  };
  setMovieRatings(nextRatings);
  invalidateRecommendations();
  const revision = bumpRepresentationRevision();
  await requestSummary(selected, nextRatings, revision);
};

  /* ---------------------------------------------------------
      REQUEST TEARS RECOMMENDATIONS
  ----------------------------------------------------------*/
  const handleRecommend = async (anotherBatch = false) => {
    if (!summary.trim()) {
      setRecommendationError(
        summaryLoading
          ? "Please wait while your taste summary is being prepared."
          : "Select and rate at least one movie before requesting recommendations."
      );
      return;
    }

    const requestId = ++recommendationRequestId.current;
    session.beginRequest();
    setLoading(true);
    setRecommendationError("");
    setRecommendations([]);

    try {
      const payload = {
        // Participant edits are signed and submitted verbatim.
        summary,
        summary_source_request_id: summarySourceRequestId,
        liked_movie_ids: selected.map((movie) => Number(movie.movieId)),
        preference_evidence: selected.map((movie) => ({
          movie_id: Number(movie.movieId),
          rating: movieRatings[movie.movieId] ?? null,
        })),
        excluded_movie_ids: session.exclusions(anotherBatch),
        ...(anotherBatch ? { exclude_previously_recommended: true } : {}),
        catalog_fingerprint: servingDeployment.matrix_fingerprint,
        onboarding_fingerprint: pilotManifest.fingerprint,
        context,
        alpha: TEARS_ALPHA,
        top_k: topK,
        min_release_year: MIN_RECOMMENDATION_YEAR,
      };
      const input = recommendationInputSnapshot("TEARS", payload);
      const meta = await study.metadata({
        taskId: "1b",
        input,
        representationRevision: representationRevisionRef.current,
      });
      // Signing is asynchronous; an edit/new click may supersede this request
      // before it is sent. Never overwrite the newer request's active receipt.
      if (requestId !== recommendationRequestId.current) return;
      payload.study = meta;
      activeRecommendationRequest.current = meta;
      currentRecommendationSignature.current = meta.input_signature;
      const res = await axios.post(`${PILOT_API_URL}/recommend`, payload);
      if (
        requestId !== recommendationRequestId.current ||
        !responseMatchesCurrent(
          activeRecommendationRequest.current,
          res.data.study,
          currentRecommendationSignature.current
        )
      ) {
        return;
      }
      const items = filterRecommendationRecords(res.data.items || [], selected,
        session.exclusions(anotherBatch).map(movieId => ({ movieId })));

      // Canonical recommendation cards and their audit receipt do not depend
      // on optional third-party posters. New titles after additions can have
      // uncached/slow metadata; show the completed ranking immediately.
      const enriched = items.map((item) => resolveMovieLensRecommendation(item, null));

      if (
        requestId === recommendationRequestId.current &&
        responseMatchesCurrent(
          activeRecommendationRequest.current,
          res.data.study,
          currentRecommendationSignature.current
        )
      ) {
        // Replace the previous result set; recommendations are never appended.
        session.display(enriched, meta);
        session.setNotice(items.length ? "" : anotherBatch ? "No unseen eligible movies remain in this session." : "No eligible recommendations match the current filters.");
        Promise.all(items.map(async (item) => {
          const metadata = await fetchPoster(item.movie_id, item.title, item.tmdb_id, item.imdb_id);
          return resolveMovieLensRecommendation(item, metadata);
        })).then((withPosters) => {
          if (
            requestId === recommendationRequestId.current &&
            responseMatchesCurrent(
              activeRecommendationRequest.current,
              res.data.study,
              currentRecommendationSignature.current
            )
          ) {
            setRecommendations(withPosters);
          }
        }).catch(() => {
          // Keep the already rendered canonical cards if optional metadata fails.
        });
      }

    } catch (err) {
      if (requestId === recommendationRequestId.current) {
        console.error("TEARS recommendation error", err);
        setRecommendationError(
          apiErrorMessage(
            err,
            "Recommendations are temporarily unavailable. Please try again."
          )
        );
      }
    } finally {
      if (requestId === recommendationRequestId.current) {
        setLoading(false);
      }
    }
  };

  // The editable summary is participant input, so refresh submits it verbatim.
  // Resume generation only if the document closed during a summary request.
  usePageRefresh(summaryPending, !loadingMovies, () => requestSummary(selected, movieRatings));
  usePageRefresh(session.refreshPending, !loadingMovies && !summaryPending && Boolean(summary.trim()), () => handleRecommend());

  /* ---------------------------------------------------------
      RANK CHANGE
  ----------------------------------------------------------*/
  // Render the eligible server ranking; metadata enrichment keeps its order.
  const renderedRecommendations = recommendations;

  /* ---------------------------------------------------------
      UI RENDER
  ----------------------------------------------------------*/
  return (
    <div
      className="theme-page min-h-screen relative overflow-x-hidden bg-gradient-to-br
      from-[#0d0017] via-[#0b0012] to-black text-white px-4 sm:px-6 py-6 sm:py-8"
    >
      {/* LOGO */}
      <Link to="/" aria-label="Go to landing page" className="absolute top-4 left-4 sm:top-6 sm:left-8 z-20">
        <motion.img
          src={publicAsset("logo.png")}
          alt="Studio Auréa"
          className="h-14 sm:h-20 drop-shadow-[0_0_35px_#00C8FF55]"
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
        />
      </Link>

      {/* BACK BUTTON */}
      <button
        onClick={goBack}
        className="absolute top-4 right-36 sm:top-6 sm:right-40 z-20 bg-white/5 border border-[#00C8FF55]
          px-4 sm:px-5 py-2 rounded-2xl text-sm sm:text-lg backdrop-blur-xl
          shadow-[0_0_20px_#00C8FF55] hover:shadow-[0_0_35px_#00C8FFAA]
          hover:border-[#00C8FF]"
      >
        ← Back
      </button>

      {/* TITLE */}
      <div className="max-w-[1600px] mx-auto mt-28 sm:mt-32 mb-8 sm:mb-10">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#80E7FF]/80 mb-3">
          Describe and refine your movie taste
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#A7E8FF] drop-shadow-[0_0_20px_#00C8FF55]">
          TEARS – Summary-Based Recommender
        </h1>
      </div>

      <div className="max-w-[1600px] mx-auto flex flex-col xl:flex-row gap-8 xl:gap-10">
        {/* ------------------------------------------------------
            LEFT PANEL — MOVIE SELECTION
        --------------------------------------------------------*/}
        <div className="w-full xl:w-3/5">
          <SelectedMovies movies={selected} system="TEARS" onRemove={toggleSelect}
            renderRating={(movie) => (
              <FiveStarRating
                key={`${study.participantId}:${study.sessionId}:${canonicalMovieLensId(movie.movieId)}`}
                movieTitle={movie.title}
                value={movieRatings[movie.movieId] ?? null}
                onChange={(rating) => handleRatingChange(movie.movieId, rating)}
              />
            )}
          />
          <h2 className="text-lg font-semibold mb-1">Movie catalog</h2>
          <p className="text-sm text-gray-400 mb-3">Select movies you like:</p>

          {loadingMovies ? (
            <div className="text-center text-gray-400 py-10">Loading…</div>
          ) : catalogMovies.length === 0 ? (
            <p className="py-6 text-sm text-gray-400">All movies in this catalog are selected. Remove a movie above to add it back.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {catalogMovies.map((m) => (
                <MoviePosterPreview key={m.movieId} movieId={m.movieId} title={m.title}
                  urls={[m.poster, ...(m.poster_urls || [])]} overview={m.overview}
                  tmdbId={m.tmdbId} imdbId={m.imdbId} year={m.year} rating={m.rating} genres={m.genres}>
                <motion.button
                  key={String(m.movieId)}
                  data-catalog-movie-id={m.movieId}
                  type="button"
                  aria-pressed={selected.some(
                    (s) =>
                      canonicalMovieLensId(s.movieId) ===
                      canonicalMovieLensId(m.movieId)
                  )}
                  onClick={() => toggleSelect(m)}
                  whileHover={{ scale: 1.08 }}
                  className={`relative cursor-pointer rounded-xl p-[3px] text-left transition-all
                    ${
                      selected.find(
                        (s) =>
                          canonicalMovieLensId(s.movieId) ===
                          canonicalMovieLensId(m.movieId)
                      )
                        ? "ring-2 ring-[#00C8FF] shadow-[0_0_20px_#00C8FF99]"
                        : "ring-1 ring-white/10 hover:ring-[#00C8FF88] hover:shadow-[0_0_20px_#00C8FF55]"
                    }`}
                >
                  <MoviePoster movieId={m.movieId} title={m.title} urls={[m.poster, ...(m.poster_urls || [])]} />

                  <p className="text-sm mt-1 text-center md:line-clamp-3">{m.title}</p>

                </motion.button>
                </MoviePosterPreview>
              ))}
            </div>
          )}
        </div>

        {/* ------------------------------------------------------
            RIGHT PANEL — SUMMARY + RECOMMENDATIONS
        --------------------------------------------------------*/}
        <div className="theme-preferences w-full xl:w-2/5 xl:sticky xl:top-6 self-start bg-white/5 border border-[#00C8FF] rounded-3xl p-4 sm:p-5
            backdrop-blur-xl shadow-[0_0_30px_#00C8FF55]">

          {/* SUMMARY */}
          {summaryError && (
            <p role="alert" className="text-sm text-red-300 mb-3">
              {summaryError}
            </p>
          )}
          <h2 className="text-lg font-semibold mb-2">Your Summary</h2>
          {summaryLoading && (
            <p className="text-xs text-[#80E7FF] mb-2">
              Generating your taste summary…
            </p>
          )}
          <textarea
            aria-label="Editable movie taste summary"
            className="w-full h-72 min-h-[18rem] sm:h-80 xl:h-[22rem] resize-y overflow-y-auto
              bg-white/5 border border-white/10 p-4 rounded-lg text-sm leading-relaxed mb-4"
            value={summary}
            onChange={(e) => {
              summaryRequestId.current += 1;
              activeSummaryRequest.current = null;
              setSummaryLoading(false);
              setSummaryPending(false);
              setSummary(e.target.value);
              bumpRepresentationRevision();
              invalidateRecommendations();
            }}
          />

          {/* Genre exclusion UI is intentionally deferred. Keep the state and
              request field in place so the feature can be restored later. */}

          <div className="mb-6">
            <label htmlFor="tears-top-k" className="text-sm font-semibold">
              Number of recommendations: {topK}
            </label>
            <input
              id="tears-top-k"
              type="range"
              min="1"
              max="25"
              value={topK}
              onChange={(e) => {
                setTopK(Number(e.target.value));
                invalidateRecommendations();
              }}
              className="w-full mt-2"
            />
          </div>

          {/* BUTTON */}
          <button
            onClick={() => handleRecommend()}
            disabled={
              loading ||
              summaryLoading ||
              !summary.trim()
            }
            className="w-full py-3 rounded-xl font-semibold text-lg bg-[#0A0A0A]
              border border-[#00C8FF] shadow-[0_0_25px_#00C8FF55]
              hover:shadow-[0_0_40px_#00C8FFAA] transition-all
              disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            {loading
              ? "Finding your movies…"
              : summaryLoading
                ? "Preparing your summary…"
                : "Get Recommendations"}
          </button>

          {session.notice && <p role="status" className="mt-3 text-sm text-gray-300">{session.notice}</p>}

          {recommendationError && (
            <p role="alert" className="mt-3 text-sm text-red-300">
              {recommendationError}
            </p>
          )}

          {/* RESULTS */}
          <RecommendationGrid system="TEARS" movies={renderedRecommendations}
            previousMovies={session.previousRecommendations} />
        </div>
      </div>
    </div>
  );
}
